import type { Screen0LanguageCode } from "@/features/language/language-registry";
import { friendlyAppName } from "../apps/friendlyAppName";
import { formatDisplayHourRange } from "../format";
import { formatTemplate } from "../../i18n/formatTemplate";
import type { Screen0Messages } from "../../i18n/messages";
import { blockWindowsOverlap, ruleWindowOverlapsBlockWindow, targetAppKey } from "../rules/ruleDerivation";
import type { RuleWindow, SmartBlockGoal } from "../rules/ruleTypes";
import type { SmartBlockSuggestion } from "./insightScoring";

export type { SmartBlockGoal };
export type SmartBlockCopy = Screen0Messages["smartBlock"];

/** Same-calendar-day window. `startHour` inclusive (0–23), `endHour` exclusive (1–24). */
export type BlockWindow = { startHour: number; endHour: number };

/** One ranking candidate: both scores, identity, and aggregated hourly signals. */
export type SmartBlockCandidate = {
  appKey: string;
  displayName: string;
  appIdentifier?: string;
  category?: string;
  color?: string;
  compulsivityScore: number;
  doomScore: number;
  totalSeconds: number;
  opens: number;
  /** Per-hour seconds across the window, length 24. */
  hourlySeconds: number[];
  /** Per-hour opens across the window, length 24. */
  hourlyOpens: number[];
};


const MIN_SCORE = 30;
const MIN_TOTAL_SECONDS = 20 * 60;
const MAX_SUGGESTIONS = 3;
/** A risk hour reaches at least this share of the peak hour. */
const RISK_PEAK_RATIO = 0.4;
/** Contiguous risk ranges longer than this collapse to a peak-centered window. */
const MAX_CONCENTRATED_HOURS = 5;
/** Spread fallback half-width: peak ± 1 hour → 3-hour window before clamping. */
const SPREAD_RADIUS = 1;
const MIN_WINDOW_HOURS = 2;
const MAX_WINDOW_HOURS = 4;

const BAND_WINDOWS: Record<string, BlockWindow> = {
  morning: { startHour: 9, endHour: 12 },
  afternoon: { startHour: 14, endHour: 17 },
  evening: { startHour: 19, endHour: 22 },
  night: { startHour: 0, endHour: 3 },
};

function positiveSum(values: number[]): number {
  return values.reduce((total, value) => total + Math.max(0, value), 0);
}

function peakHourOf(signal: number[]): number {
  let peak = -1;
  let peakHour = 0;
  for (let hour = 0; hour < signal.length; hour += 1) {
    const value = Math.max(0, signal[hour] ?? 0);
    if (value > peak) {
      peak = value;
      peakHour = hour;
    }
  }
  return peakHour;
}


const SMART_BLOCK_GOALS: SmartBlockGoal[] = ["compulsivity", "doom"];

function scoreForGoal(candidate: SmartBlockCandidate, goal: SmartBlockGoal): number {
  return goal === "compulsivity" ? candidate.compulsivityScore : candidate.doomScore;
}

type MetricDraft = {
  candidate: SmartBlockCandidate;
  goal: SmartBlockGoal;
  score: number;
  window: BlockWindow;
  signal: number[];
  basis: "opens" | "seconds";
};

/** Per app, keep higher-scoring metrics first and drop lower-ranked windows that overlap. */
function resolveNonOverlappingMetricDrafts(drafts: MetricDraft[]): MetricDraft[] {
  const sorted = [...drafts].sort((a, b) => b.score - a.score);
  const kept: MetricDraft[] = [];
  for (const draft of sorted) {
    if (kept.some((existing) => blockWindowsOverlap(existing.window, draft.window))) continue;
    kept.push(draft);
  }
  return kept;
}

function blockWindowBlockedByRules(window: BlockWindow, blockedRules: RuleWindow[]): boolean {
  return blockedRules.some((rule) => ruleWindowOverlapsBlockWindow(rule, window));
}

function candidateBlockWindows(signal: number[], fallbackWindow: BlockWindow | null): BlockWindow[] {
  const candidates: BlockWindow[] = [];
  const seen = new Set<string>();

  const add = (window: BlockWindow | null) => {
    if (!window) return;
    const length = window.endHour - window.startHour;
    if (length < MIN_WINDOW_HOURS || length > MAX_WINDOW_HOURS) return;
    const key = `${window.startHour}:${window.endHour}`;
    if (seen.has(key)) return;
    seen.add(key);
    candidates.push(window);
  };

  add(detectBlockWindow(signal));
  const peak = peakHourOf(signal);
  for (let hour = 0; hour < 24; hour += 1) {
    if (Math.max(0, signal[hour] ?? 0) > 0) {
      add(spreadWindow(hour));
    }
  }
  add(spreadWindow(peak));
  for (const window of Object.values(BAND_WINDOWS)) {
    add(window);
  }
  add(fallbackWindow);

  return candidates;
}

/**
 * Picks the best usage-aligned block window that does not overlap enabled Smart
 * Block rules for the same app. Returns null when no valid 2–4h slot exists.
 */
export function resolveBlockWindowAvoidingRules(
  signal: number[],
  blockedRules: RuleWindow[],
  fallbackWindow: BlockWindow | null,
): BlockWindow | null {
  if (positiveSum(signal) <= 0) {
    if (fallbackWindow && !blockWindowBlockedByRules(fallbackWindow, blockedRules)) {
      return fallbackWindow;
    }
    return null;
  }

  const candidates = candidateBlockWindows(signal, fallbackWindow);
  if (candidates.length === 0) return null;

  const viable = candidates.filter((window) => !blockWindowBlockedByRules(window, blockedRules));
  if (viable.length === 0) return null;

  viable.sort((a, b) => shareInWindow(signal, b) - shareInWindow(signal, a));
  return viable[0] ?? null;
}

/** Peak ± 1 hour, clamped to the same day, enforcing the 2–4h length bounds. */
function spreadWindow(peakHour: number): BlockWindow {
  let startHour = Math.max(0, peakHour - SPREAD_RADIUS);
  let endHour = Math.min(24, peakHour + SPREAD_RADIUS + 1);

  if (endHour - startHour < MIN_WINDOW_HOURS) {
    if (startHour === 0) endHour = Math.min(24, startHour + MIN_WINDOW_HOURS);
    else startHour = Math.max(0, endHour - MIN_WINDOW_HOURS);
  }
  if (endHour - startHour > MAX_WINDOW_HOURS) endHour = startHour + MAX_WINDOW_HOURS;
  return { startHour, endHour };
}

/**
 * Concentrated-usage window detection (PRD 7.2/7.3). Returns the longest run of
 * hours at or above 40% of the peak hour; a single-hour or day-spanning run
 * falls back to a peak-centered spread window. Returns null when the signal is
 * empty (caller substitutes a coarse band window).
 */
export function detectBlockWindow(signal: number[]): BlockWindow | null {
  const total = positiveSum(signal);
  if (total <= 0) return null;

  const peak = Math.max(...signal.map((value) => Math.max(0, value)));
  const peakHour = peakHourOf(signal);
  const threshold = peak * RISK_PEAK_RATIO;

  const ranges: Array<{ start: number; end: number }> = [];
  let current: { start: number; end: number } | null = null;
  for (let hour = 0; hour < 24; hour += 1) {
    const value = Math.max(0, signal[hour] ?? 0);
    if (value > 0 && value >= threshold) {
      if (current) current.end = hour;
      else current = { start: hour, end: hour };
    } else if (current) {
      ranges.push(current);
      current = null;
    }
  }
  if (current) ranges.push(current);
  if (ranges.length === 0) return spreadWindow(peakHour);

  let best = ranges[0]!;
  for (const range of ranges) {
    const length = range.end - range.start + 1;
    const bestLength = best.end - best.start + 1;
    if (length > bestLength) {
      best = range;
    } else if (length === bestLength) {
      const rangeHasPeak = peakHour >= range.start && peakHour <= range.end;
      const bestHasPeak = peakHour >= best.start && peakHour <= best.end;
      if (rangeHasPeak && !bestHasPeak) best = range;
    }
  }

  const length = best.end - best.start + 1;
  if (length < MIN_WINDOW_HOURS || length > MAX_CONCENTRATED_HOURS) {
    return spreadWindow(peakHour);
  }
  return { startHour: best.start, endHour: best.end + 1 };
}

/** Coarse device-band fallback for apps with no per-app hourly data (PRD 7.4). */
export function coarseBandWindow(bandSeconds: Record<string, number>): BlockWindow | null {
  let bestKey: string | null = null;
  let bestSeconds = 0;
  for (const [key, seconds] of Object.entries(bandSeconds)) {
    if (BAND_WINDOWS[key] && seconds > bestSeconds) {
      bestSeconds = seconds;
      bestKey = key;
    }
  }
  return bestKey ? BAND_WINDOWS[bestKey]! : null;
}

function selectWindowSignal(
  candidate: SmartBlockCandidate,
  goal: SmartBlockGoal,
): { signal: number[]; basis: "opens" | "seconds" } {
  if (goal === "compulsivity" && positiveSum(candidate.hourlyOpens) > 0) {
    return { signal: candidate.hourlyOpens, basis: "opens" };
  }
  return { signal: candidate.hourlySeconds, basis: "seconds" };
}

function shareInWindow(signal: number[], window: BlockWindow): number {
  const total = positiveSum(signal);
  if (total <= 0) return 0;
  let inside = 0;
  for (let hour = window.startHour; hour < window.endHour && hour < 24; hour += 1) {
    inside += Math.max(0, signal[hour] ?? 0);
  }
  return inside / total;
}

export function formatHourRange(
  startHour: number,
  endHour: number,
  languageCode: Screen0LanguageCode = "en",
): string {
  return formatDisplayHourRange(startHour, endHour, languageCode);
}

function toSuggestion(
  candidate: SmartBlockCandidate,
  goal: SmartBlockGoal,
  window: BlockWindow,
  signal: number[],
  basis: "opens" | "seconds",
  copy: SmartBlockCopy,
  languageCode: Screen0LanguageCode,
): SmartBlockSuggestion {
  const name = friendlyAppName(candidate);
  const range = formatHourRange(window.startHour, window.endHour, languageCode);
  const sharePct = positiveSum(signal) > 0 ? Math.round(shareInWindow(signal, window) * 100) : 0;

  let reason: string;
  if (goal === "doom") {
    reason = sharePct > 0
      ? formatTemplate(copy.reasonDoomShare, { sharePct, name, range })
      : formatTemplate(copy.reasonDoomCluster, { name, range });
  } else {
    const opener = basis === "opens"
      ? formatTemplate(copy.reasonCompulsivityOpens, { name, range })
      : formatTemplate(copy.reasonCompulsivityTime, { name, range });
    reason = sharePct > 0
      ? `${opener} ${copy.reasonCompulsivityShareSuffix}`
      : formatTemplate(copy.reasonCompulsivityRepeat, { name, range });
  }

  return {
    id: `${goal}:${candidate.appKey}`,
    appKey: candidate.appKey,
    displayName: candidate.displayName,
    appIdentifier: candidate.appIdentifier,
    category: candidate.category,
    goal,
    goalLabel: goal === "doom" ? copy.goalDoom : copy.goalCompulsivity,
    startHour: window.startHour,
    endHour: window.endHour,
    actionLabel: formatTemplate(copy.actionLabel, { range }),
    reason,
  };
}

/**
 * Builds ranked full-block suggestions, up to two per app (one per metric) when
 * both qualify with non-overlapping windows. `fallbackWindow` covers apps without
 * per-app hourly data.
 */
export function buildSmartBlockSuggestions(
  candidates: SmartBlockCandidate[],
  fallbackWindow: BlockWindow | null,
  copy: SmartBlockCopy,
  maxSuggestions = MAX_SUGGESTIONS,
  blockedRuleWindowsByApp?: ReadonlyMap<string, RuleWindow[]>,
  languageCode: Screen0LanguageCode = "en",
): SmartBlockSuggestion[] {
  const perAppDrafts: MetricDraft[] = [];

  for (const candidate of candidates) {
    if (candidate.totalSeconds < MIN_TOTAL_SECONDS) continue;

    const metricDrafts: MetricDraft[] = [];
    for (const goal of SMART_BLOCK_GOALS) {
      const score = scoreForGoal(candidate, goal);
      if (score < MIN_SCORE) continue;
      const { signal, basis } = selectWindowSignal(candidate, goal);
      const blockedRules =
        blockedRuleWindowsByApp?.get(candidate.appKey) ??
        blockedRuleWindowsByApp?.get(
          targetAppKey({ displayName: candidate.displayName, appIdentifier: candidate.appIdentifier }),
        ) ??
        [];
      const window = resolveBlockWindowAvoidingRules(signal, blockedRules, fallbackWindow);
      if (!window) continue;
      metricDrafts.push({ candidate, goal, score, window, signal, basis });
    }

    perAppDrafts.push(...resolveNonOverlappingMetricDrafts(metricDrafts));
  }

  const ranked = perAppDrafts.sort(
    (a, b) =>
      b.score - a.score ||
      b.candidate.totalSeconds - a.candidate.totalSeconds ||
      b.candidate.opens - a.candidate.opens ||
      friendlyAppName(a.candidate).localeCompare(friendlyAppName(b.candidate)) ||
      a.goal.localeCompare(b.goal),
  );

  const suggestions: SmartBlockSuggestion[] = [];
  for (const { candidate, goal, window, signal, basis } of ranked) {
    if (suggestions.length >= maxSuggestions) break;
    suggestions.push(toSuggestion(candidate, goal, window, signal, basis, copy, languageCode));
  }
  return suggestions;
}
